import { Component, Input, OnDestroy, ViewEncapsulation, OnInit, computed, signal } from '@angular/core';
import { ReplaySubject, takeUntil } from 'rxjs';

import { simpleFadeAnimation } from 'app/commons/overlay-transition/overlay-transition-animation';
import { delayFor } from 'app/core/utilities/async-utils';
import { Loggable } from 'app/utils/logging/loggable';
import User from 'entity/User';

import { DevicePermissionService } from '../../services/device-permission/device-permission.service';
import { PermissionsService } from '../../services/permissions.service';
import { fadeInTransition, fadeOutTransition } from './fade-transition.animation';
import Social from 'entity/Social';
import { RoomPresenceService } from '../../services/room/room-presence.service';
import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { Store } from '@ngrx/store';
import { socialAppSelectors } from '../../state/app';

@Component({
  selector: 'app-welcome-room-transition',
  templateUrl: './welcome-room-transition.component.html',
  styleUrls: ['./welcome-room-transition.component.scss'],
  animations: [simpleFadeAnimation, fadeInTransition, fadeOutTransition],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class WelcomeRoomTransitionComponent extends Loggable implements OnDestroy, OnInit {
  @Input() public user?: User;
  @Input() public isHost?: boolean;
  @Input() public social?: Social;

  protected showOnboardingMessage?: boolean;
  protected handleOnboardingScenario?: boolean;
  protected isVisible?: boolean;
  protected isWaitingForHost = signal<boolean>(true);
  protected welcomeText = computed(() =>
    this.isWaitingForHost() ? 'Waiting on the host to start the meeting.' : 'We are placing you in a room.',
  );
  protected onboardingExperience = signal<boolean>(false);
  protected hasBadgeQuestions = signal<boolean>(false);

  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor(
    private roomPresenceService: RoomPresenceService,
    private permissionsService: PermissionsService,
    private devicePermissionService: DevicePermissionService,
    private store: Store,
  ) {
    super();

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.onboarding_experience)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((onboarding_experience) => {
        this.onboardingExperience.set(onboarding_experience);
      });

    this.store
      .select(socialAppSelectors.hasBadgeQuestions)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((hasBadgeQuestions) => this.hasBadgeQuestions.set(hasBadgeQuestions));
  }

  public ngOnInit(): void {
    const onboardingMessage = localStorage.getItem('showOnboardingMessage');
    if (onboardingMessage === 'true') {
      this.isVisible = true;
      this.showOnboardingMessage = true;
      void this.playOnboardingAudio();
      void this.hideMessage();
    }

    this.isWaitingForHost.set(this.isHost || this.social?.isOpen || this.social?.anyoneCanStart ? false : true);
  }

  /**
   * Getters and setters
   */
  public get shouldShowAvatarVideo(): boolean {
    return this.devicePermissionService.shouldShowAvatarVideo();
  }

  public get shouldShowAvatarPicture(): boolean {
    return this.devicePermissionService.shouldShowAvatarPicture();
  }

  public onVideoPlaying = (): void => {
    this.devicePermissionService.onVideoPlaying();
  };

  public onVideoTimeout = (): void => {
    this.devicePermissionService.onVideoTimeout();
  };

  public async ngOnDestroy(): Promise<void> {
    try {
      await this.permissionsService.cleanAllStreams();
    } catch (e) {
      this.error('Error cleaning up streams:', e);
    } finally {
      localStorage.removeItem('showOnboardingMessage');
      this.destroyed$.next(true);
      this.destroyed$.complete();
    }
  }

  private async playOnboardingAudio(): Promise<void> {
    const audio = new Audio();
    audio.id = 'onboarding-audio';
    audio.src = './assets/video/onboarding-experience/5.mp4';

    if (!audio.paused) {
      audio.pause();
    } else {
      try {
        await audio.play();
      } catch {
        alert('Error while trying to play the video:');
      }
    }
  }

  private async hideMessage(): Promise<void> {
    await delayFor(2000);
    this.isVisible = false;
    localStorage.removeItem('showOnboardingMessage');
  }

  public redirectToButtonAction(step?: string): void {
    if (step) {
      this.roomPresenceService.handleWaitingForHostStep(step);
    }
  }
}
